<template>
  <div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-12 text-left">
        <label class="control-label">{{ $t("stock_management.orders.order_type") }}</label>
        <DxRadioGroup
          v-model="localData.order_type_id"
          :data-source="orderTypesList"
          :class="{ 'field-error': this.$v.localData.order_type_id.$error }"
          display-expr="name"
          value-expr="id"
          layout="horizontal"
          @value-changed="onOrderTypeChanged"
          ref="orderType"
        />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("common.legal_entity_name") }}</label>
        <DxSelectBox
          v-model="localData.legal_entity_uuid"
          :data-source="getLegalEntityList()"
          display-expr="name"
          value-expr="uuid"
          :on-value-changed="onLegalEntityChanged"
          :class="{
            'field-error': this.$v.localData.legal_entity_uuid?.$error,
          }"
          placeholder=""
        />
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("stock_management.orders.date") }}</label>
        <date-picker2
          v-model="localData.date"
          :value-type="'format'"
          :format="getDefaultDateFormat"
          :placeholder="getDefaultDateFormat"
          type="date"
          :input-class="['date-picker-style']"
          :class="{ 'field-error': this.$v.localData.date.$error }"
          @change="onDateChanged"
          ref="datePicker"
        ></date-picker2>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("stock_management.orders.order_number") }}</label>
        <DxAutocomplete
          v-model="localData.order_number"
          :data-source="[]"
          :min-search-length="0"
          :show-clear-button="true"
          :class="{
            'field-error': this.$v.localData.order_number?.$error,
          }"
        />
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("common.partner") }}</label>
        <div
          class="sm-select-input-dialog-btn"
          @click="partnerClick"
          :class="{
            'field-error': this.$v.localData.partner_uuid?.$error,
          }"
        >
          <div class="sm-select-input-dialog-info">
            <div class="sm-select-input-dialog-value">{{ getPartnerNames }}</div>
          </div>
          <div class="sm-select-input-dialog-icon">
            <i class="fa-regular fa-edit"></i>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left"></div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("common.delivery_date") }}</label>
        <date-picker2
          v-model="localData.delivery_date"
          :value-type="'format'"
          :format="getDefaultDateFormat"
          :placeholder="getDefaultDateFormat"
          type="date"
          :input-class="['date-picker-style']"
          @change="onDeliveryDateChanged"
          ref="datePickerDelivery"
        ></date-picker2>
      </div>
    </div>
    <PartnerSelector
      ref="partnerSelector"
      :visible="showPartnerDialog"
      @close="showPartnerDialog = false"
      @continue="onContinueFromPartnerSelector"
      :multiselect="false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxRadioGroup } from "devextreme-vue/radio-group";
import DatePicker2 from "vue2-datepicker";
import required from "vuelidate/src/validators/required";
import requiredIf from "vuelidate/src/validators/requiredIf";
import moment from "moment";
import DxAutocomplete from "devextreme-vue/autocomplete";
import PartnerSelector from "@/components/PartnerSelector.vue";
import { ORDER_TYPE } from "@/pages/stock-management/orders/config/orderTypes";

export default {
  name: "settings",
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localData: { ...this.formData },
      orderTypesList: [],
      showPartnerDialog: false,
    };
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
      departments: "company/departments",
    }),
    getPartnerNames() {
      if (!this.localData.partner_name) {
        return " - ";
      }
      return this.localData.partner_name;
    },
  },
  components: {
    DxSelectBox,
    DxRadioGroup,
    DatePicker2,
    DxAutocomplete,
    PartnerSelector,
  },
  methods: {
    ...mapActions({
      getSmOrderTypes: "smOrders/getSmOrderTypes",
      getDepartments: "company/getDepartments",
    }),
    onDateChanged(date) {},
    onDeliveryDateChanged(date) {},
    onContinueFromPartnerSelector(selectedPartners) {
      this.localData.partner_uuid = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0].uuid : null;
      this.localData.partner_name = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0].name : null;
      this.showPartnerDialog = false;
    },
    partnerClick() {
      this.showPartnerDialog = true; // Show the PartnerSelector dialog
    },
    onLegalEntityChanged(e) {
      if (e.value && this.departments.length > 0) {
        const selectedEntity = this.departments.find((dept) => dept.legal_entity?.uuid === e.value);
        if (selectedEntity) {
          this.localData.legal_entity_name = selectedEntity.legal_entity.name;
        }
      }
    },
    onOrderTypeChanged(e) {
      if (e.value && this.orderTypesList.length > 0) {
        this.localData.order_type = this.orderTypesList.find((type) => parseInt(type.id) === parseInt(e.value))?.name;
      }
    },
    getLegalEntityList() {
      return this.departments
        .filter((item) => item.is_legal_entity === 1 && item.legal_entity)
        .map((item) => ({
          uuid: item.legal_entity.uuid,
          name: item.legal_entity.name,
        }));
    },
    validate() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return [this.$t("fill_out_error")];
      }
      return [];
    },
    updateData(newData) {
      if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
        this.localData = { ...newData };

        if (this.$refs.orderType) {
          this.$refs.orderType.instance.option("value", this.localData.order_type_id);
        }
      }
    },
  },
  mounted() {
    if (!this.localData.uuid && !this.localData.date) {
      this.localData.date = moment().format(this.getDefaultDateFormat);
    }

    this.getDepartments({ limit: 1000 });

    this.getSmOrderTypes().then((response) => {
      if (response && response.length > 0) {
        this.orderTypesList = response.map((item) => ({
          id: parseInt(item.id),
          name: item.name,
          disabled: item.disabled ?? false,
        }));

        if (!this.localData.order_type_id) {
          const internalOrderType = this.orderTypesList.find((type) => type.id === ORDER_TYPE.PURCHASE);
          this.localData.order_type_id = internalOrderType?.id;
          this.localData.order_type = internalOrderType?.name;
        }
      }
    });
  },
  watch: {
    formData: {
      deep: true,
      handler(newData) {
        if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
          this.localData = { ...newData };
        }
      },
    },
    localData: {
      deep: true,
      handler(newData) {
        this.$emit("input", newData);
        this.$emit("update-form-data", newData, 1);
      },
    },
  },
  validations() {
    return {
      localData: {
        order_type_id: {
          required,
        },
        date: {
          required,
        },
        partner_uuid: {
          required: requiredIf(function () {
            return Number(this.localData.order_type_id) !== ORDER_TYPE.INTERNAL;
          }),
        },
        legal_entity_uuid: {
          required,
        },
        order_number: {
          required,
        },
      },
    };
  },
};
</script>
<style scoped></style>
